import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@wa/ui";

/**
 * The centred dialog: blurred scrim, a 32px-radius card, a title row with the
 * close disc, and a footer whose buttons share the width equally.
 *
 * Esc stands down while a Radix popper is open, so one press closes the select
 * that is on top rather than the dialog underneath it.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  headExtra,
  className,
  label,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Sits between the title and the close disc - a line picker, a status pill. */
  headExtra?: ReactNode;
  className?: string;
  label?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    // `is-css-in` opts into the keyframed entrance. Plain DOM, no framer here,
    // so nothing else is writing to these properties - see the note by
    // `.crm-scrim` in index.css.
    <div
      className="crm-scrim is-css-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={cn("crm-modal is-css-in", className)} role="dialog" aria-modal="true" aria-label={label}>
        <div className="crm-m-head">
          <span className="crm-m-t">{title}</span>
          {headExtra}
          <button type="button" className="crm-m-x" onClick={onClose} aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        {children}
        {footer ? <div className="crm-m-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
